import { updateTimeTravelDopTime } from './TimeTravel';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { RootState } from '@app/store';
import { getSettingsState } from '@app/store/globals';
import { Satellite } from '@app/store/gnssm/codegen/gnssmApi';

import {
  Coordinates,
  CustomStation,
  DOPType,
  GNSS,
  GroundStation,
  StationSelection,
} from '@app/models';

import { startOfHour } from '@app/utils/Datetime';

export interface SettingsState {
  showSatelliteDetails: boolean;
  showSatelliteCount: boolean;
  switchDOPType: boolean;
  visibleDOPType: DOPType;
  systemsEnabled: Array<GNSS>;
  visibleStationsSelected: StationSelection;
  selectedStation: GroundStation | CustomStation | null;
  selectedSatellite: Satellite | null;
  selectedTime: number;
  selectedUtcTime: string;
  selectedCoords: Coordinates | null;
  currentView: [number, number, number];
  TECState: boolean;
  selectedElevationMask: number;
}

/* ************************* *
 *    Utitilty functions     *
 * ************************* */

export const dopTypesExplanation = {
  DOP: 'Dilution of precision is an indicator of satellite geometry for constellation of satellites used to determine a position. Lower the DOP value, better the position accuracy.',
  GDOP: 'Geometric Dilution Of Precision',
  PDOP: 'Position Dilution Of Precision',
  HDOP: 'Horizontal Dilution Of Precision',
  VDOP: 'Vertical Dilution Of Precision',
  TDOP: 'Time Dilution Of Precision',
};

/* ********************* *
 *    Slice creation     *
 * ********************* */
const initialState: SettingsState = {
  showSatelliteDetails: false,
  showSatelliteCount: false,
  switchDOPType: false,
  visibleDOPType: DOPType.GDOP,
  systemsEnabled: [GNSS.Galileo],
  visibleStationsSelected: StationSelection.PerformanceAnalysis,
  selectedStation: null,
  selectedSatellite: null,
  selectedTime: startOfHour().valueOf(),
  selectedUtcTime: startOfHour().toISOString(),
  selectedCoords: null,
  currentView: [40, 0, 2],
  TECState: false,
  selectedElevationMask: 5,
};

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    setShowSatelliteStationDetails: (state: SettingsState, action: PayloadAction<boolean>) => {
      state.showSatelliteDetails = action.payload;
    },
    setShowSatelliteCount: (state: SettingsState, action: PayloadAction<boolean>) => {
      state.showSatelliteCount = action.payload;
      // turn DOP and tec layer off when satellite count layer is activated
      if (action.payload) {
        state.switchDOPType = false;
        state.TECState = false;
      }
    },
    setSwitchDOPType: (state: SettingsState, action: PayloadAction<boolean>) => {
      state.switchDOPType = action.payload;
      // turn satellite count and tec layer off when DOP is activated
      if (action.payload) {
        state.showSatelliteCount = false;
        state.TECState = false;
      }
    },
    setVisibleDOPType: (state: SettingsState, action: PayloadAction<DOPType>) => {
      state.visibleDOPType = action.payload;
    },
    setTECState: (state: SettingsState, action: PayloadAction<boolean>) => {
      state.TECState = action.payload;
      // turn dop and satellite count layer off when tec is activated
      if (action.payload) {
        state.switchDOPType = false;
        state.showSatelliteCount = false;
      }
    },
    toggleSystem: (state: SettingsState, action: PayloadAction<GNSS>) => {
      if (state.systemsEnabled.includes(action.payload)) {
        state.systemsEnabled = state.systemsEnabled.filter((s) => s !== action.payload);
      } else {
        state.systemsEnabled = [action.payload, ...state.systemsEnabled];
      }
    },
    setSystemsEnabled: (state: SettingsState, action: PayloadAction<GNSS[]>) => {
      state.systemsEnabled = action.payload;
    },
    setVisibleStationsSelected: (state: SettingsState, action: PayloadAction<StationSelection>) => {
      // this reducer also resets the currently selected station to null
      state.visibleStationsSelected = action.payload;

      // Reset station selection when switching between visible stations.
      // Do not reset if we switch to selection 'All'
      if (action.payload !== StationSelection.All) {
        state.selectedStation = null;
      }
    },
    setSelectedStation: (
      state: SettingsState,
      action: PayloadAction<GroundStation | CustomStation | null>,
    ) => {
      if (action == null || state.selectedStation != action.payload) {
        // if station is unselected or changed then clear satellite selection
        state.selectedSatellite = null;
      }
      state.selectedStation = action.payload;

      // reset selected coords
      state.selectedCoords = null;
    },
    setSelectedSatellite: (state: SettingsState, action: PayloadAction<Satellite | null>) => {
      state.selectedSatellite = action.payload;
      // reset selected coords
      state.selectedCoords = null;
    },
    setSelectedTime: (state: SettingsState, action: PayloadAction<number>) => {
      state.selectedTime = action.payload;
      state.selectedUtcTime = new Date(action.payload).toISOString(); // assumes that passes timestamp is utc time
    },
    setSelectedUtcTime: (state: SettingsState, action: PayloadAction<string>) => {
      state.selectedUtcTime = action.payload;
      state.selectedTime = new Date(action.payload).getTime();
    },
    setCurrentView: (state: SettingsState, action: PayloadAction<[number, number, number]>) => {
      state.currentView = action.payload;
    },
    updateObs: (state: SettingsState, action: PayloadAction<Coordinates | null>) => {
      if (action.payload === null) {
        state.selectedCoords = null;
      } else {
        state.selectedCoords = {
          // set default values
          latitude: 0,
          longitude: 0,
          height: 0,
          elevation: 0,
          // override with previous values
          ...state.selectedCoords,
          // override with new values
          ...action.payload,
        };
      }
    },
    setSelectedElevationMask: (state: SettingsState, action: PayloadAction<number>) => {
      state.selectedElevationMask = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(updateTimeTravelDopTime.type, (state: SettingsState) => ({
      ...state,
      selectedTime: Date.now(),
    }));
  },
});

/* *********************** *
 *    Export selectors     *
 * *********************** */

export const getShowSatelliteDetails = (state: RootState) =>
  getSettingsState(state).showSatelliteDetails;
export const getVisibleDopType = (state: RootState) => getSettingsState(state).visibleDOPType;
export const getSystemsEnabled = (state: RootState) => getSettingsState(state).systemsEnabled;
export const getVisibleStationsSelected = (state: RootState) =>
  getSettingsState(state).visibleStationsSelected;
export const getSelectedUtcTime = (state: RootState) => getSettingsState(state).selectedUtcTime;
export const getSelectedStation = (state: RootState) => getSettingsState(state).selectedStation;
export const getSelectedSatellite = (state: RootState) => getSettingsState(state).selectedSatellite;
export const getSwitchDOPType = (state: RootState) => getSettingsState(state).switchDOPType;
export const getCurrentView = (state: RootState) => getSettingsState(state).currentView;
export const getVisibleTECState = (state: RootState) => getSettingsState(state).TECState;
export const getShowSatelliteCount = (state: RootState) =>
  getSettingsState(state).showSatelliteCount;
export const getSelectedElevationMask = (state: RootState) =>
  getSettingsState(state).selectedElevationMask;

/* ********************* *
 *    Export actions     *
 * ********************* */

export const {
  setShowSatelliteStationDetails,
  setShowSatelliteCount,
  setSwitchDOPType,
  setVisibleDOPType,
  toggleSystem,
  setSystemsEnabled,
  setVisibleStationsSelected,
  setSelectedStation,
  setSelectedTime,
  setSelectedUtcTime,
  setSelectedSatellite,
  setCurrentView,
  updateObs,
  setTECState,
  setSelectedElevationMask,
} = settingsSlice.actions;

/* ********************* *
 *    Export reducer     *
 * ********************* */
export default settingsSlice.reducer;
